import Link from "next/link";
import Image from "next/image";
import { type PortfolioProject } from "@/app/lib/portfolio";
import PortfolioPagination from "./PortfolioPagination";

const PROJECTS_PER_PAGE = 4;

interface PortfolioGridProps {
  projects: PortfolioProject[];
  page: number;
  onPageChange?: (page: number) => void;
}

export default function PortfolioGrid({ projects, page, onPageChange }: PortfolioGridProps) {
  const totalPages = Math.ceil(projects.length / PROJECTS_PER_PAGE);
  const safePage = Math.min(page, totalPages || 1);
  const visibleProjects = projects.slice(
    (safePage - 1) * PROJECTS_PER_PAGE,
    safePage * PROJECTS_PER_PAGE,
  );

  if (visibleProjects.length === 0) {
    return (
      <div className="border border-surface-variant bg-surface-lowest p-12 text-center text-secondary w-full">
        No projects match your search or filter.
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="grid w-full grid-cols-1 md:grid-cols-2 gap-gutter justify-items-center">
        {visibleProjects.map((project, index) => {
          const href = `/portfolio/${project.slug}`;

          return (
            <Link
              key={project.slug}
              href={href}
              className="group w-full max-w-[36rem] border border-surface-variant bg-surface-lowest hover:border-primary transition-all duration-300 flex flex-col hover:shadow-lg"
            >
              <article className="flex flex-col w-full h-full">
                <div className="relative w-full aspect-[16/9] border-b border-surface-variant overflow-hidden">
                  <Image
                  priority={index === 0}
                    alt={project.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 group-hover:opacity-100 transition-all duration-700 ease-out"
                    src={project.imageUrl}
                    width={800}
                    height={450}
                    sizes="(max-width: 640px) 100vw, (max-width: 1200px) 50vw, 800px"
                  />
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  {project.categories.length > 0 ? (
                    <div className="flex gap-2 mb-6 flex-wrap">
                      {project.categories.map((category) => (
                        <span
                          key={`${project.slug}-${category}`}
                          className="inline-flex items-center bg-surface-container-low text-primary font-label-sm text-label-sm px-2 py-1 rounded tracking-widest uppercase"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <h2 className="font-h2 text-h2 text-primary mb-4">
                    {project.title}
                  </h2>
                  <p className="font-body-md text-body-md text-secondary mb-8 flex-grow">
                    {project.description}
                  </p>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
      <PortfolioPagination totalPages={totalPages} onPageChangeExternal={onPageChange} />
    </div>
  );
}
